/**
 * 🎨 CELL LAYOUT SETTINGS COMPONENT
 * 
 * Ausgelagerte Komponente für Cell Layout/Alignment Controls
 * Wird in GridFloatingToolbar verwendet
 */

import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounce';

function CellLayoutSettings({
    gridElement,
    onStyleChange,
    device = 'desktop',
    gridFlowMode = 'fixed' // ✅ Brauchen wir um zu wissen, ob Min/Max Width angezeigt werden soll
}) {
    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte
    const [localStyles, setLocalStyles] = useState({
        cellDefaultGap: gridElement?.cellDefaultGap ?? 12,
        cellDefaultGapMobile: gridElement?.cellDefaultGapMobile ?? 12,
        cellDefaultMinWidth: gridElement?.cellDefaultMinWidth ?? 200,
        cellDefaultMinWidthMobile: gridElement?.cellDefaultMinWidthMobile ?? 150,
        cellDefaultMaxWidth: gridElement?.cellDefaultMaxWidth ?? 800,
        cellDefaultMaxWidthMobile: gridElement?.cellDefaultMaxWidthMobile ?? 800,
        // ✅ Individuelles Padding (Top, Right, Bottom, Left)
        cellDefaultPaddingTop: gridElement?.cellDefaultPaddingTop ?? 15,
        cellDefaultPaddingRight: gridElement?.cellDefaultPaddingRight ?? 15,
        cellDefaultPaddingBottom: gridElement?.cellDefaultPaddingBottom ?? 15,
        cellDefaultPaddingLeft: gridElement?.cellDefaultPaddingLeft ?? 15,
        cellDefaultPaddingTopMobile: gridElement?.cellDefaultPaddingTopMobile ?? 12,
        cellDefaultPaddingRightMobile: gridElement?.cellDefaultPaddingRightMobile ?? 12,
        cellDefaultPaddingBottomMobile: gridElement?.cellDefaultPaddingBottomMobile ?? 12,
        cellDefaultPaddingLeftMobile: gridElement?.cellDefaultPaddingLeftMobile ?? 12
    });
    
    // ⚡ Update local styles when gridElement changes
    useEffect(() => {
        setLocalStyles({
            cellDefaultGap: gridElement?.cellDefaultGap ?? 12,
            cellDefaultGapMobile: gridElement?.cellDefaultGapMobile ?? 12,
            cellDefaultMinWidth: gridElement?.cellDefaultMinWidth ?? 200,
            cellDefaultMinWidthMobile: gridElement?.cellDefaultMinWidthMobile ?? 150,
            cellDefaultMaxWidth: gridElement?.cellDefaultMaxWidth ?? 800,
            cellDefaultMaxWidthMobile: gridElement?.cellDefaultMaxWidthMobile ?? 800,
            // ✅ Individuelles Padding (Top, Right, Bottom, Left)
            cellDefaultPaddingTop: gridElement?.cellDefaultPaddingTop ?? 15,
            cellDefaultPaddingRight: gridElement?.cellDefaultPaddingRight ?? 15,
            cellDefaultPaddingBottom: gridElement?.cellDefaultPaddingBottom ?? 15,
            cellDefaultPaddingLeft: gridElement?.cellDefaultPaddingLeft ?? 15,
            cellDefaultPaddingTopMobile: gridElement?.cellDefaultPaddingTopMobile ?? 12,
            cellDefaultPaddingRightMobile: gridElement?.cellDefaultPaddingRightMobile ?? 12,
            cellDefaultPaddingBottomMobile: gridElement?.cellDefaultPaddingBottomMobile ?? 12,
            cellDefaultPaddingLeftMobile: gridElement?.cellDefaultPaddingLeftMobile ?? 12
        });
    }, [gridElement]);
    
    // ⚡ DEBOUNCED Firebase Update
    const debouncedStyleChange = useDebouncedCallback((updates) => {
        onStyleChange(updates);
    }, 100);
    
    const cellFlexDirection = device === 'mobile'
        ? (gridElement?.cellDefaultFlexDirectionMobile ?? gridElement?.cellDefaultFlexDirection ?? 'column')
        : (gridElement?.cellDefaultFlexDirection ?? 'column');
    
    return (
        <div className="grid-expanded-section">
            {/* Content Direction - Desktop/Mobile separate */}
            <div className="grid-control-group-full">
                <label className="grid-control-label">
                    Content Direction {device === 'mobile' ? '(📱 Mobile)' : '(💻 Desktop)'}
                </label>
                <div className="grid-direction-buttons">
                    <button
                        className={`grid-direction-btn ${cellFlexDirection === 'column' ? 'active' : ''}`}
                        onClick={() => {
                            if (device === 'mobile') {
                                onStyleChange({ cellDefaultFlexDirectionMobile: 'column' });
                            } else {
                                onStyleChange({ cellDefaultFlexDirection: 'column' });
                            }
                        }}
                        title="Vertical (Stack)"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                        <span>Vertical</span>
                    </button>
                    <button
                        className={`grid-direction-btn ${cellFlexDirection === 'row' ? 'active' : ''}`}
                        onClick={() => {
                            if (device === 'mobile') {
                                onStyleChange({ cellDefaultFlexDirectionMobile: 'row' });
                            } else {
                                onStyleChange({ cellDefaultFlexDirection: 'row' });
                            }
                        }}
                        title="Horizontal (Side by Side)"
                    >
                        <i className="fas fa-arrows-alt-h"></i>
                        <span>Horizontal</span>
                    </button>
                </div>
            </div>
            
            {/* Content Alignment - Vereinfachte Visuelle Controls */}
            <div className="grid-control-group-full" style={{ marginTop: '16px' }}>
                <label className="grid-control-label" style={{ marginBottom: '12px' }}>
                    <i className="fas fa-align-center" style={{ marginRight: '6px' }}></i>
                    Content Alignment in Cells
                </label>
                
                {/* ✅ SIMPLIFIED: Nur die relevanten Optionen basierend auf Direction */}
                {cellFlexDirection === 'row' ? (
                    <>
                        {/* Bei ROW Layout (Icon + Text nebeneinander) */}
                        <div className="grid-alignment-group">
                            <span className="grid-alignment-label">
                                <i className="fas fa-arrows-alt-h" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                                Horizontal Position
                            </span>
                            <div className="grid-alignment-buttons-extended">
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'flex-start' })}
                                    title="Align Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span className="grid-btn-label">Left</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'center' })}
                                    title="Align Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span className="grid-btn-label">Center</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'flex-end' })}
                                    title="Align Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span className="grid-btn-label">Right</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'space-between' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'space-between' })}
                                    title="Space Between - Maximum space between elements"
                                >
                                    <i className="fas fa-grip-lines-vertical"></i>
                                    <span className="grid-btn-label">Between</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'space-around' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'space-around' })}
                                    title="Space Around - Equal space around elements"
                                >
                                    <i className="fas fa-border-all"></i>
                                    <span className="grid-btn-label">Around</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'flex-start') === 'space-evenly' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'space-evenly' })}
                                    title="Space Evenly - Perfectly equal spacing"
                                >
                                    <i className="fas fa-equals"></i>
                                    <span className="grid-btn-label">Evenly</span>
                                </button>
                            </div>
                        </div>
                        
                        <div className="grid-alignment-group">
                            <span className="grid-alignment-label">
                                <i className="fas fa-arrows-alt-v" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                                Vertical Align
                            </span>
                            <div className="grid-alignment-buttons">
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'flex-start' })}
                                    title="Top"
                                >
                                    <i className="fas fa-arrow-up"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Top</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-grip-lines"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Center</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'flex-end' })}
                                    title="Bottom"
                                >
                                    <i className="fas fa-arrow-down"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Bottom</span>
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Bei COLUMN Layout (Vertical Stack) */}
                        <div className="grid-alignment-group">
                            <span className="grid-alignment-label">
                                <i className="fas fa-arrows-alt-h" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                                Horizontal Align
                            </span>
                            <div className="grid-alignment-buttons">
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'flex-start' })}
                                    title="Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'flex-end' })}
                                    title="Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultAlignItems || 'flex-start') === 'stretch' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultAlignItems: 'stretch' })}
                                    title="Stretch Full Width"
                                >
                                    <i className="fas fa-arrows-alt-h"></i>
                                </button>
                            </div>
                        </div>
                        
                        <div className="grid-alignment-group">
                            <span className="grid-alignment-label">
                                <i className="fas fa-arrows-alt-v" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                                Position (Top/Center/Bottom)
                            </span>
                            <div className="grid-alignment-buttons">
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'center') === 'flex-start' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'flex-start' })}
                                    title="Top"
                                >
                                    <i className="fas fa-arrow-up"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Top</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'center') === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-grip-lines"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Center</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'center') === 'flex-end' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'flex-end' })}
                                    title="Bottom"
                                >
                                    <i className="fas fa-arrow-down"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Bottom</span>
                                </button>
                                <button
                                    className={`grid-alignment-btn ${(gridElement?.cellDefaultJustifyContent || 'center') === 'space-between' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultJustifyContent: 'space-between' })}
                                    title="Space Between"
                                >
                                    <i className="fas fa-arrows-alt-v"></i>
                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>Space</span>
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            {/* ✅ NEW: Gap between Cell Content Elements */}
            <div className="grid-control-group" style={{ marginTop: '16px' }}>
                <label className="grid-control-label">
                    <i className="fas fa-grip-lines" style={{ marginRight: '6px' }}></i>
                    Gap between Elements
                </label>
                <input
                    type="range"
                    min="0"
                    max="40"
                    step="2"
                    value={device === 'mobile'
                        ? (localStyles.cellDefaultGapMobile ?? gridElement?.cellDefaultGapMobile ?? localStyles.cellDefaultGap ?? gridElement?.cellDefaultGap ?? 12)
                        : (localStyles.cellDefaultGap ?? gridElement?.cellDefaultGap ?? 12)}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        // ⚡ OPTIMISTIC: Instant local update
                        if (device === 'mobile') {
                            setLocalStyles(prev => ({ ...prev, cellDefaultGapMobile: val }));
                            debouncedStyleChange({ cellDefaultGapMobile: val });
                        } else {
                            setLocalStyles(prev => ({ ...prev, cellDefaultGap: val }));
                            debouncedStyleChange({ cellDefaultGap: val });
                        }
                    }}
                    className="grid-slider"
                />
                <span className="grid-control-value">
                    {device === 'mobile'
                        ? (localStyles.cellDefaultGapMobile ?? gridElement?.cellDefaultGapMobile ?? localStyles.cellDefaultGap ?? gridElement?.cellDefaultGap ?? 12)
                        : (localStyles.cellDefaultGap ?? gridElement?.cellDefaultGap ?? 12)}px
                </span>
            </div>
            
            {/* Min Width - NUR bei auto-fit mode */}
            {gridFlowMode === 'auto-fit' && (
                <div className="grid-control-group">
                    <label className="grid-control-label">Min Width</label>
                    <input
                        type="range"
                        min="100"
                        max="800"
                        step="20"
                        value={device === 'mobile' 
                            ? (localStyles.cellDefaultMinWidthMobile ?? gridElement?.cellDefaultMinWidthMobile ?? 150)
                            : (localStyles.cellDefaultMinWidth ?? gridElement?.cellDefaultMinWidth ?? 200)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            // ⚡ OPTIMISTIC: Instant local update
                            if (device === 'mobile') {
                                setLocalStyles(prev => ({ ...prev, cellDefaultMinWidthMobile: val }));
                                debouncedStyleChange({ cellDefaultMinWidthMobile: val });
                            } else {
                                setLocalStyles(prev => ({ ...prev, cellDefaultMinWidth: val }));
                                debouncedStyleChange({ cellDefaultMinWidth: val });
                            }
                        }}
                        className="grid-slider"
                    />
                    <span className="grid-control-value">
                        {device === 'mobile' 
                            ? (localStyles.cellDefaultMinWidthMobile ?? gridElement?.cellDefaultMinWidthMobile ?? 150)
                            : (localStyles.cellDefaultMinWidth ?? gridElement?.cellDefaultMinWidth ?? 200)}px
                    </span>
                </div>
            )}
            
            {/* Max Width - IMMER verfügbar */}
            <div className="grid-control-group">
                <label className="grid-control-label">Max Width</label>
                <input
                    type="range"
                    min="200"
                    max="2000"
                    step="50"
                    value={device === 'mobile' 
                        ? (localStyles.cellDefaultMaxWidthMobile ?? gridElement?.cellDefaultMaxWidthMobile ?? 800)
                        : (localStyles.cellDefaultMaxWidth ?? gridElement?.cellDefaultMaxWidth ?? 800)}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        // ⚡ OPTIMISTIC: Instant local update
                        if (device === 'mobile') {
                            setLocalStyles(prev => ({ ...prev, cellDefaultMaxWidthMobile: val }));
                            debouncedStyleChange({ cellDefaultMaxWidthMobile: val });
                        } else {
                            setLocalStyles(prev => ({ ...prev, cellDefaultMaxWidth: val }));
                            debouncedStyleChange({ cellDefaultMaxWidth: val });
                        }
                    }}
                    className="grid-slider"
                />
                <span className="grid-control-value">
                    {device === 'mobile' 
                        ? (localStyles.cellDefaultMaxWidthMobile ?? gridElement?.cellDefaultMaxWidthMobile ?? 800)
                        : (localStyles.cellDefaultMaxWidth ?? gridElement?.cellDefaultMaxWidth ?? 800)}px
                </span>
            </div>
            
            {/* ✨ PADDING SECTION - Individual Controls */}
            <div className="grid-control-group-full" style={{ marginTop: '20px' }}>
                <label className="grid-control-label" style={{ marginBottom: '12px' }}>
                    <i className="fas fa-compress-arrows-alt" style={{ marginRight: '6px' }}></i>
                    Cell Padding (Inner Spacing)
                </label>
                
                {/* Padding Top */}
                <div className="grid-control-group">
                    <label className="grid-control-label">
                        <i className="fas fa-arrow-up" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                        Top
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="80"
                        step="2"
                        value={device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingTopMobile ?? gridElement?.cellDefaultPaddingTopMobile ?? 12)
                            : (localStyles.cellDefaultPaddingTop ?? gridElement?.cellDefaultPaddingTop ?? 15)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            // ⚡ OPTIMISTIC: Instant local update
                            if (device === 'mobile') {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingTopMobile: val }));
                                debouncedStyleChange({ cellDefaultPaddingTopMobile: val });
                            } else {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingTop: val }));
                                debouncedStyleChange({ cellDefaultPaddingTop: val });
                            }
                        }}
                        className="grid-slider"
                    />
                    <span className="grid-control-value">
                        {device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingTopMobile ?? gridElement?.cellDefaultPaddingTopMobile ?? 12)
                            : (localStyles.cellDefaultPaddingTop ?? gridElement?.cellDefaultPaddingTop ?? 15)}px
                    </span>
                </div>
                
                {/* Padding Right */}
                <div className="grid-control-group">
                    <label className="grid-control-label">
                        <i className="fas fa-arrow-right" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                        Right
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="80"
                        step="2"
                        value={device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingRightMobile ?? gridElement?.cellDefaultPaddingRightMobile ?? 12)
                            : (localStyles.cellDefaultPaddingRight ?? gridElement?.cellDefaultPaddingRight ?? 15)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            // ⚡ OPTIMISTIC: Instant local update
                            if (device === 'mobile') {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingRightMobile: val }));
                                debouncedStyleChange({ cellDefaultPaddingRightMobile: val });
                            } else {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingRight: val }));
                                debouncedStyleChange({ cellDefaultPaddingRight: val });
                            }
                        }}
                        className="grid-slider"
                    />
                    <span className="grid-control-value">
                        {device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingRightMobile ?? gridElement?.cellDefaultPaddingRightMobile ?? 12)
                            : (localStyles.cellDefaultPaddingRight ?? gridElement?.cellDefaultPaddingRight ?? 15)}px
                    </span>
                </div>
                
                {/* Padding Bottom */}
                <div className="grid-control-group">
                    <label className="grid-control-label">
                        <i className="fas fa-arrow-down" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                        Bottom
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="80"
                        step="2"
                        value={device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingBottomMobile ?? gridElement?.cellDefaultPaddingBottomMobile ?? 12)
                            : (localStyles.cellDefaultPaddingBottom ?? gridElement?.cellDefaultPaddingBottom ?? 15)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            // ⚡ OPTIMISTIC: Instant local update
                            if (device === 'mobile') {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingBottomMobile: val }));
                                debouncedStyleChange({ cellDefaultPaddingBottomMobile: val });
                            } else {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingBottom: val }));
                                debouncedStyleChange({ cellDefaultPaddingBottom: val });
                            }
                        }}
                        className="grid-slider"
                    />
                    <span className="grid-control-value">
                        {device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingBottomMobile ?? gridElement?.cellDefaultPaddingBottomMobile ?? 12)
                            : (localStyles.cellDefaultPaddingBottom ?? gridElement?.cellDefaultPaddingBottom ?? 15)}px
                    </span>
                </div>
                
                {/* Padding Left */}
                <div className="grid-control-group">
                    <label className="grid-control-label">
                        <i className="fas fa-arrow-left" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                        Left
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="80"
                        step="2"
                        value={device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingLeftMobile ?? gridElement?.cellDefaultPaddingLeftMobile ?? 12)
                            : (localStyles.cellDefaultPaddingLeft ?? gridElement?.cellDefaultPaddingLeft ?? 15)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            // ⚡ OPTIMISTIC: Instant local update
                            if (device === 'mobile') {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingLeftMobile: val }));
                                debouncedStyleChange({ cellDefaultPaddingLeftMobile: val });
                            } else {
                                setLocalStyles(prev => ({ ...prev, cellDefaultPaddingLeft: val }));
                                debouncedStyleChange({ cellDefaultPaddingLeft: val });
                            }
                        }}
                        className="grid-slider"
                    />
                    <span className="grid-control-value">
                        {device === 'mobile' 
                            ? (localStyles.cellDefaultPaddingLeftMobile ?? gridElement?.cellDefaultPaddingLeftMobile ?? 12)
                            : (localStyles.cellDefaultPaddingLeft ?? gridElement?.cellDefaultPaddingLeft ?? 15)}px
                    </span>
                </div>
            </div>
        </div>
    );
}

export default CellLayoutSettings;

